import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { MaintenanceRequestRepository } from '~/repositories/maintenanceRequestRepository';
import { portfolioRepository } from '~/repositories/portfolioRepository';
import { propertiesRepository } from '~/repositories/propertiesRepository';
import { PropertyManagersRepository } from '~/repositories/propertyManagersRepository';
import { vendorRepo } from '~/repositories/vendorRepository';
import { maintenanceServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
const propertyManagersRepository = new PropertyManagersRepository();
const uniqueRunBookIds = (ids: (string | null | undefined)[]): string[] => [...new Set(ids.filter((id): id is string => !!id))];
import type { MaintenanceRequestFilter, HydratedMaintenanceRequestsList } from '~/repositories/maintenanceRequestRepository';
import type {
  AddPropertyManagerOverwriteRequestDto,
  AddVendorOverwriteRequestDto,
  CreateRunBookRequestDto,
  RunBookDto,
  RunBooksPaginationDto,
  UpdateDefaultPropertyManagerRequestDto,
  UpdateDefaultVendorRequestDto,
  UpdateRunBookRequestDto,
} from '~/swagger/maintenance/Api';

const BASE_URL = `${maintenanceServiceUrl()}/company-portal/run-books`;

export class runBooksRepository extends BaseRestRepository {
  public async getRunBooks(filters: any): Promise<RunBooksPaginationDto> {
    const url = BASE_URL;
    const response = await this.fetchWithAuth(urlWithQuery(url, filters));
    // Maintenance now returns the generic IDs-only page; map to the UI's shape.
    // The list view only reads `items`; per-runbook vendor/PM come from the detail.
    const page = (await this.getJsonResponse(response)) as Pick<RunBooksPaginationDto, 'items'> & {
      page: number;
      totalPages: number;
      pageSize: number;
      totalCount: number;
    };
    return {
      currentPage: page.page,
      totalPages: page.totalPages,
      pageSize: page.pageSize,
      totalCount: page.totalCount,
      items: page.items,
      vendors: [],
      propertyManagers: [],
    };
  }

  // The Maintenance service returns the runbook detail IDs-only; reconstruct the
  // vendor/property-manager/property/portfolio lookups formatRunBookData expects.
  private async hydrateRunBookDetail(raw: any): Promise<RunBookDto> {
    const vendorIds = uniqueRunBookIds([raw.defaultVendorId, ...(raw.vendorOverwrites ?? []).map((o: any) => o.vendorId)]);
    const propertyIds = uniqueRunBookIds(
      (raw.vendorOverwrites ?? []).concat(raw.propertyManagerOverwrites ?? []).map((o: any) => o.propertyId)
    );
    const portfolioIds = uniqueRunBookIds(
      (raw.vendorOverwrites ?? []).concat(raw.propertyManagerOverwrites ?? []).map((o: any) => o.portfolioId)
    );

    const [vendors, properties, portfolios, propertyManagers] = await Promise.all([
      vendorIds.length ? vendorRepo.batchGetVendorsByIds(vendorIds) : Promise.resolve([]),
      propertyIds.length ? propertiesRepository.batchGetPropertyByIds(propertyIds) : Promise.resolve([]),
      portfolioIds.length ? portfolioRepository.batchGetPortfolioByIds(portfolioIds) : Promise.resolve([]),
      propertyManagersRepository.getPropertyManagers(),
    ]);

    return { ...raw, vendors, properties, portfolios, propertyManagers } as RunBookDto;
  }

  public async getRunBookById(runBookId: string): Promise<RunBookDto> {
    const url = `${BASE_URL}/${runBookId}`;
    const response = await this.fetchWithAuth(url);
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }

  public async enableRunBook(runBookId: string): Promise<void> {
    const url = `${BASE_URL}/${runBookId}/enable`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
    });
  }

  public async disableRunBook(runBookId: string): Promise<void> {
    const url = `${BASE_URL}/${runBookId}/disable`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
    });
  }

  public async deleteRunBook(runBookId: string) {
    const url = `${BASE_URL}/${runBookId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async addRunBook(payload: CreateRunBookRequestDto): Promise<RunBookDto> {
    const url = BASE_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }
  public async updateRunBook(runBookId: string, payload: UpdateRunBookRequestDto): Promise<void> {
    const url = `${BASE_URL}/${runBookId}`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  public async getRunBookMaintenanceRequest(
    filters: Omit<MaintenanceRequestFilter, 'runBookId'> & { runBookId: string }
  ): Promise<HydratedMaintenanceRequestsList> {
    const maintenanceRequestRepository = new MaintenanceRequestRepository();
    return maintenanceRequestRepository.getMaintenanceRequests(filters);
  }

  public async updateDefaultVendor(runBookId: string, payload: UpdateDefaultVendorRequestDto): Promise<RunBookDto> {
    const url = `${BASE_URL}/${runBookId}/vendor`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }

  public async updateDefaultPropertyManager(runBookId: string, payload: UpdateDefaultPropertyManagerRequestDto): Promise<RunBookDto> {
    const url = `${BASE_URL}/${runBookId}/property-manager`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }

  public async addVendorOverride(runBookId: string, payload: AddVendorOverwriteRequestDto): Promise<RunBookDto> {
    const url = `${BASE_URL}/${runBookId}/overwrite/vendors`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }

  public async addPropertyManagersOverride(runBookId: string, payload: AddPropertyManagerOverwriteRequestDto): Promise<RunBookDto> {
    const url = `${BASE_URL}/${runBookId}/overwrite/property-managers`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }

  public async deleteVendorOverride(runBookId: string, overwriteId: string) {
    const url = `${BASE_URL}/${runBookId}/overwrite/vendors/${overwriteId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }
  public async deletePropertyManagersOverride(runBookId: string, overwriteId: string) {
    const url = `${BASE_URL}/${runBookId}/overwrite/property-managers/${overwriteId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.hydrateRunBookDetail(await this.getJsonResponse(response));
  }
}
